import * as THREE from 'three';
import { SAFE_ZONE_VERTEX_CHUNK, safeZoneUniforms } from '../../shaders/safeZone.glsl.js';

// THE CROWD: procedural silhouettes, by distance in pixels (R1, R13), one
// instanced draw. Every person is an archetype made of independent choices -
// eight hair cuts (short, long, ponytail, bun, cap, bob, afro, shaved), five
// gestures (arms down, one up, both up, clapping, pointing), a height, a
// breadth, a head size - so no two neighbours repeat. The body is a black
// with a blue cast; a rim of light rides on the tops of the heads and the
// shoulders, in the colour of the screens behind them and of the beams that
// pass overhead (computed per person in the vertex shader). No textures, no
// sprite, nothing that can leave a white artefact on a face.
//
// Four depth bands, from forty people at the barrier to the bulk at the back;
// the back is smaller (perspective) and darker. Animation per person: a
// bounce on the beat with its own phase and amplitude, a nod, the arms rising
// with the build, a collective JUMP on the drop as a wave from the centre,
// phones held up (a small sharp lit screen), a few people on shoulders.

export const BEAMS_MAX = 16;
export const FLAGS = 6;

const crowdVertex = `
  precision highp float;
  attribute vec4 aPos;           // x, z, height scale, breadth
  attribute vec4 aLook;          // hair, head size, rider, phone
  attribute vec4 aMove;          // phase, amplitude, arm threshold, gesture
  uniform float uBeats;          // the beat count (float)
  uniform float uTime;
  uniform float uBounce;         // 0..1, the beat's bounce
  uniform float uSway;           // calm: a slow sway
  uniform float uArms;           // 0..1, how far the build has raised the arms
  uniform float uPhones;         // 0..1, phones up (calm)
  uniform float uJumpT;          // the drop's time (seconds), -1e9 when none
  uniform sampler2D uZones;
  uniform vec3 uBeamO[${BEAMS_MAX}];
  uniform vec3 uBeamD[${BEAMS_MAX}];
  uniform vec3 uBeamC[${BEAMS_MAX}];
  uniform float uBeamN;
  uniform float uRimGain;
  ${SAFE_ZONE_VERTEX_CHUNK}
  varying vec2 vP;
  varying vec4 vLook;
  varying vec4 vArm;             // left up, right up, gesture, nod
  varying vec3 vRim;
  varying float vFade;
  varying float vPhone;
  varying float vSafe;
  void main() {
    float h = aPos.z, w = aPos.w;
    vec3 base = vec3(aPos.x, aLook.z > 0.5 ? 1.15 : 0.0, aPos.y);
    float b = uBeats + aMove.x;
    float fb = fract(b);
    float amp = aMove.y;
    // the bounce: down on the beat, back up
    base.y -= 0.07 * amp * uBounce * (1.0 - smoothstep(0.0, 0.55, fb));
    base.x += 0.06 * uSway * sin(uTime * 1.2 + aMove.x * 6.28);
    // the jump: a wave from the centre of the barrier, 9 m/s
    float dist = length(vec2(aPos.x, aPos.y - 3.0));
    float tj = uTime - uJumpT - dist / 9.0;
    float jump = (tj > 0.0 && tj < 0.62) ? sin(3.14159 * tj / 0.62) * (0.38 + 0.2 * amp) : 0.0;
    base.y += jump;
    float up = clamp((uArms - aMove.z) * 4.0, 0.0, 1.0);
    up = max(up, jump > 0.01 ? 1.0 : 0.0);
    if (aLook.z > 0.5) up = 1.0;
    float phone = aLook.w * uPhones;
    vArm = vec4(up * (aMove.w > 0.5 ? 1.0 : 0.0), max(up, phone), aMove.w, 0.04 * amp * exp(-fb * 6.0) * uBounce);
    vPhone = phone;
    vLook = aLook;
    vec3 toCam = cameraPosition - base; toCam.y = 0.0;
    vec3 fwd = length(toCam) > 1e-4 ? normalize(toCam) : vec3(0.0, 0.0, 1.0);
    vec3 right = normalize(cross(vec3(0.0, 1.0, 0.0), fwd));
    vec2 local = vec2(position.x * 0.62 * w, (position.y + 0.5) * 2.5 * h);
    vP = vec2(local.x / w, local.y / h);
    vec3 world = base + right * local.x + vec3(0.0, local.y, 0.0);
    gl_Position = projectionMatrix * viewMatrix * vec4(world, 1.0);
    vSafe = safeZoneAttenuation(gl_Position);
    // light: the screens behind (by x), and every beam that passes over this head
    vec3 top = base + vec3(0.0, 1.75 * h, 0.0);
    vec3 rim = texture2D(uZones, vec2(clamp(aPos.x / 30.0 + 0.5, 0.02, 0.98), 0.5)).rgb * uRimGain;
    for (int i = 0; i < ${BEAMS_MAX}; i++) {
      if (float(i) >= uBeamN) break;
      vec3 v = top - uBeamO[i];
      float t = dot(v, uBeamD[i]);
      if (t > 0.0) {
        float r = 0.35 + t * 0.09;
        float perp = length(v - uBeamD[i] * t);
        rim += uBeamC[i] * exp(-perp * perp / (r * r)) * 2.2;
      }
    }
    vRim = rim;
    vFade = mix(1.0, 0.42, smoothstep(8.0, 38.0, aPos.y));
  }
`;

const crowdFragment = `
  precision highp float;
  varying vec2 vP;
  varying vec4 vLook;
  varying vec4 vArm;
  varying vec3 vRim;
  varying float vFade;
  varying float vPhone;
  varying float vSafe;
  uniform float uFrontLit;
  vec3 cCaps(vec2 p, vec2 a, vec2 b, float ra, float rb) {
    vec2 pa = p - a, ba = b - a;
    float hh = clamp(dot(pa, ba) / max(dot(ba, ba), 1e-6), 0.0, 1.0);
    vec2 q = pa - ba * hh;
    float l = max(length(q), 1e-5);
    return vec3(l - mix(ra, rb, hh), q / l);
  }
  vec3 cEll(vec2 p, vec2 c, vec2 r) {
    vec2 q = (p - c) / r;
    float l = max(length(q), 1e-5);
    return vec3((l - 1.0) * min(r.x, r.y), normalize(q / r));
  }
  vec3 cU(vec3 a, vec3 b) { return a.x < b.x ? a : b; }
  void main() {
    vec2 p = vP;
    if (p.y > 2.45 || abs(p.x) > 0.62) discard;
    float hs = 0.9 + 0.2 * vLook.y;
    vec2 hc = vec2(0.0, 1.62 - vArm.w);
    // body
    vec3 d = cCaps(p, vec2(0.0, 0.92), vec2(0.0, 1.33), 0.15, 0.17);
    d = cU(d, cCaps(p, vec2(-0.19, 1.4), vec2(0.19, 1.4), 0.075, 0.075));
    d = cU(d, cCaps(p, vec2(0.0, 1.4), hc, 0.05, 0.05));
    d = cU(d, cCaps(p, vec2(-0.09, 0.9), vec2(-0.11, 0.0), 0.08, 0.06));
    d = cU(d, cCaps(p, vec2(0.09, 0.9), vec2(0.11, 0.0), 0.08, 0.06));
    // arms: down, or up (by gesture), interpolated
    float g = vArm.z;
    for (int s = 0; s < 2; s++) {
      float sd = s == 0 ? -1.0 : 1.0;
      float up = s == 0 ? vArm.x : vArm.y;
      vec2 sh = vec2(0.2 * sd, 1.4);
      vec2 eD = vec2(0.215 * sd, 1.13), hD = vec2(0.205 * sd, 0.9);
      vec2 eU = vec2(0.3 * sd, 1.68), hU = vec2(0.34 * sd, 1.98);
      if (g > 2.5 && g < 3.5) { eU = vec2(0.24 * sd, 1.72); hU = vec2(0.05 * sd, 2.06); }        // clapping overhead
      else if (g > 3.5 && s == 1) { eU = vec2(0.36, 1.62); hU = vec2(0.52, 1.88); }                // pointing
      vec2 e = mix(eD, eU, up), hd = mix(hD, hU, up);
      d = cU(d, cCaps(p, sh, e, 0.05, 0.045));
      d = cU(d, cCaps(p, e, hd, 0.042, 0.036));
      d = cU(d, cEll(p, hd, vec2(0.045)));
    }
    // head and hair
    float hair = vLook.x;
    vec2 hr = vec2(0.095, 0.115) * hs;
    if (hair > 5.5 && hair < 6.5) hr = vec2(0.15, 0.15) * hs;             // afro
    d = cU(d, cEll(p, hc, hr));
    if (hair > 0.5 && hair < 1.5) d = cU(d, cCaps(p, hc + vec2(0.0, 0.02), hc + vec2(0.0, -0.24), 0.11 * hs, 0.1 * hs));   // long
    else if (hair > 1.5 && hair < 2.5) d = cU(d, cCaps(p, hc + vec2(0.05, 0.07), hc + vec2(0.14, -0.17), 0.04, 0.025)); // ponytail
    else if (hair > 2.5 && hair < 3.5) d = cU(d, cEll(p, hc + vec2(0.0, 0.135 * hs), vec2(0.055)));                      // bun
    else if (hair > 3.5 && hair < 4.5) d = cU(d, cCaps(p, hc + vec2(-0.1, 0.06) * hs, hc + vec2(0.14, 0.05) * hs, 0.03, 0.018)); // cap
    else if (hair > 4.5 && hair < 5.5) d = cU(d, cCaps(p, hc + vec2(0.0, 0.03), hc + vec2(0.0, -0.08), 0.125 * hs, 0.125 * hs));    // bob
    // a phone in the right hand, its screen lit
    float screen = 0.0;
    if (vPhone > 0.01) {
      vec2 hd = mix(vec2(0.205, 0.9), vec2(0.34, 1.98), vArm.y);
      vec2 q = abs(p - hd - vec2(0.0, 0.07)) - vec2(0.035, 0.06);
      float pd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
      d = cU(d, vec3(pd, 0.0, 1.0));
      float pxp = max(length(fwidth(p)), 1e-4);
      // sharp when large enough, never under one lit pixel when far (R7)
      screen = max(clamp(0.5 - (pd + 0.008) / pxp, 0.0, 1.0), exp(-max(pd, 0.0) / (0.8 * pxp)) * 0.85) * vPhone;
    }
    float px = max(length(fwidth(p)), 1e-4);
    float cov = clamp(0.5 - d.x / px, 0.0, 1.0);
    if (cov <= 0.0) discard;
    // the rim: 1.5-2.5 px on the edges that face UP (heads, shoulders, raised arms), never a full outline (R13);
    // on a figure only a few pixels tall it would fill the body, so it thins out with the size on screen
    float band = 1.0 - smoothstep(0.0, min(2.2 * px, 0.035), -d.x);
    float face = smoothstep(0.25, 0.85, d.z);
    float small = clamp(0.06 / px, 0.0, 1.0);
    vec3 col = vec3(0.003, 0.004, 0.009) * vFade;
    col += vRim * band * face * vFade * mix(0.35, 1.0, small);
    // seen from the stage, the screens behind the camera light the faces
    col += vRim * 0.06 * uFrontLit * vFade * cov;
    col = mix(col, vec3(0.85, 0.9, 1.0), screen);
    gl_FragColor = vec4(col * vSafe, cov);
  }
`;

// --- the flags -----------------------------------------------------------------------------
const flagVertex = `
  precision highp float;
  attribute vec4 aFlag;          // x, z, phase, colour index
  uniform float uTime;
  uniform vec3 uFlagCol[4];
  ${SAFE_ZONE_VERTEX_CHUNK}
  varying vec2 vUv;
  varying vec3 vCol;
  varying float vSafe;
  void main() {
    vec3 base = vec3(aFlag.x, 0.0, aFlag.y);
    vec3 toCam = cameraPosition - base; toCam.y = 0.0;
    vec3 fwd = length(toCam) > 1e-4 ? normalize(toCam) : vec3(0.0, 0.0, 1.0);
    vec3 right = normalize(cross(vec3(0.0, 1.0, 0.0), fwd));
    vUv = position.xy + 0.5;
    // the pole from 1.9 to 3.6 m, the cloth hanging off its top, waving
    float x = vUv.x * 0.95, y = 2.2 + vUv.y * 1.25;
    float wave = sin(uTime * 3.0 + aFlag.z - x * 3.0) * 0.12 * x;
    vec3 world = base + right * x + vec3(0.0, y + wave, 0.0) + fwd * wave * 0.5;
    gl_Position = projectionMatrix * viewMatrix * vec4(world, 1.0);
    vSafe = safeZoneAttenuation(gl_Position);
    vCol = uFlagCol[int(mod(aFlag.w, 4.0))];
  }
`;
const flagFragment = `
  precision highp float;
  uniform float uTimeF;
  varying vec2 vUv; varying vec3 vCol; varying float vSafe;
  void main() {
    // the pole on the left edge, the cloth from 0.55 up
    float pole = step(vUv.x, 0.025);
    float cloth = step(0.42, vUv.y) * step(0.03, vUv.x);
    if (pole + cloth < 0.5) discard;
    vec3 c = pole > 0.5 ? vec3(0.004) : vCol * (0.07 + 0.06 * sin(vUv.x * 9.0 - uTimeF * 3.0));
    gl_FragColor = vec4(c * vSafe, 1.0);
  }
`;

function rng(seed) { let s = seed >>> 0 || 1; return () => { s = (s * 16807) % 2147483647; return s / 2147483647; }; }

export class StageCrowd {
  constructor(scene, count, zonesTexture, opts = {}) {
    this._scene = scene;
    const R = rng(opts.seed || 4242);
    const N = Math.max(40, count | 0);
    // the bands: [z0, z1, share]
    const bands = [[2.8, 4.4, 0], [4.4, 11.0, 0.15], [11.0, 22.0, 0.30], [22.0, 40.0, 1]];
    const front = Math.min(40, Math.round(N * 0.05));
    const counts = [front, Math.round(N * 0.15), Math.round(N * 0.30)];
    counts.push(N - counts[0] - counts[1] - counts[2]);
    const people = [];
    bands.forEach(([z0, z1], k) => {
      const n = counts[k];
      const placed = [];
      const minD = k === 0 ? 0.62 : k === 1 ? 0.5 : 0.42;
      let tries = 0;
      while (placed.length < n && tries < n * 60) {
        tries++;
        const z = z0 + R() * (z1 - z0);
        const half = 10.5 + (z - 3) * 0.36;
        const x = (R() * 2 - 1) * half;
        let ok = true;
        for (let j = Math.max(0, placed.length - 160); j < placed.length; j++) { const q = placed[j]; if ((q[0] - x) ** 2 + (q[1] - z) ** 2 < minD * minD) { ok = false; break; } }
        if (!ok && tries < n * 40) continue;
        placed.push([x, z]);
      }
      for (const [x, z] of placed) people.push([x, z]);
    });
    people.sort((a, b) => a[1] - b[1]);
    const n = people.length;
    const pos = new Float32Array(n * 4), look = new Float32Array(n * 4), move = new Float32Array(n * 4);
    for (let i = 0; i < n; i++) {
      const [x, z] = people[i];
      pos[i * 4] = x; pos[i * 4 + 1] = z; pos[i * 4 + 2] = 0.88 + R() * 0.24; pos[i * 4 + 3] = 0.85 + R() * 0.35;
      look[i * 4] = Math.floor(R() * 8); look[i * 4 + 1] = R();
      look[i * 4 + 2] = z > 6 && z < 30 && R() < 0.018 ? 1 : 0;              // on someone's shoulders
      look[i * 4 + 3] = R() < 0.09 ? 1 : 0;                                  // a phone
      move[i * 4] = R() * 0.3; move[i * 4 + 1] = 0.5 + R() * 0.7; move[i * 4 + 2] = R() * 0.95; move[i * 4 + 3] = Math.floor(R() * 5);
    }
    const geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0]), 3));
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    geo.setAttribute('aPos', new THREE.InstancedBufferAttribute(pos, 4));
    geo.setAttribute('aLook', new THREE.InstancedBufferAttribute(look, 4));
    geo.setAttribute('aMove', new THREE.InstancedBufferAttribute(move, 4));
    geo.instanceCount = n;
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 1, 20), 60);
    this._geo = geo;
    const O = [], D = [], C = [];
    for (let i = 0; i < BEAMS_MAX; i++) { O.push(new THREE.Vector3()); D.push(new THREE.Vector3(0, -1, 0)); C.push(new THREE.Vector3()); }
    this.uniforms = Object.assign({}, safeZoneUniforms, {
      uBeats: { value: 0 }, uTime: { value: 0 }, uBounce: { value: 1 }, uSway: { value: 0 }, uArms: { value: 0 }, uPhones: { value: 0 },
      uJumpT: { value: -1e9 }, uZones: { value: zonesTexture }, uBeamO: { value: O }, uBeamD: { value: D }, uBeamC: { value: C }, uBeamN: { value: 0 }, uRimGain: { value: 1 }, uFrontLit: { value: 0 }
    });
    this._mat = new THREE.ShaderMaterial({ uniforms: this.uniforms, vertexShader: crowdVertex, fragmentShader: crowdFragment, transparent: true, depthWrite: true, depthTest: true, side: THREE.DoubleSide, extensions: { derivatives: true } });
    this.mesh = new THREE.Mesh(geo, this._mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 20;
    scene.add(this.mesh);
    this.count = n;
    // flags: a few, held up in the middle bands
    if (opts.flags) {
      const fa = new Float32Array(FLAGS * 4);
      const mid = people.filter((q) => q[1] > 8 && q[1] < 24);
      for (let i = 0; i < FLAGS; i++) { const p = mid[Math.floor(mid.length * R())] || people[0]; fa[i * 4] = p[0] + 0.2; fa[i * 4 + 1] = p[1]; fa[i * 4 + 2] = R() * 6.28; fa[i * 4 + 3] = i % 4; }
      const fg = new THREE.InstancedBufferGeometry();
      fg.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0]), 3));
      fg.setIndex([0, 1, 2, 0, 2, 3]);
      fg.setAttribute('aFlag', new THREE.InstancedBufferAttribute(fa, 4));
      fg.instanceCount = FLAGS;
      fg.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 3, 20), 60);
      this._flagGeo = fg;
      this.flagUniforms = Object.assign({}, safeZoneUniforms, { uTime: this.uniforms.uTime, uTimeF: this.uniforms.uTime, uFlagCol: { value: [0, 1, 2, 3].map(() => new THREE.Vector3(0.6, 0.1, 0.2)) } });
      this._flagMat = new THREE.ShaderMaterial({ uniforms: this.flagUniforms, vertexShader: flagVertex, fragmentShader: flagFragment, side: THREE.DoubleSide, transparent: true, depthWrite: true });
      this.flags = new THREE.Mesh(fg, this._flagMat);
      this.flags.frustumCulled = false; this.flags.renderOrder = 21;
      scene.add(this.flags);
    }
  }
  dispose() {
    this._scene.remove(this.mesh); this._mat.dispose(); this._geo.dispose();
    if (this.flags) { this._scene.remove(this.flags); this._flagMat.dispose(); this._flagGeo.dispose(); }
  }
}
